/**
 * SKU-first DTC variant pricing.
 *
 * Pack variants (4-pack / 6-pack) prefer product_sku.rrpPack, then the
 * dtc_pricing_cans grid, then unit × count. Slab stays formula-only.
 */

function formatPrice(value) {
  const num = typeof value === 'number' ? value : parseFloat(value);
  if (Number.isNaN(num)) return null;
  return num.toFixed(2);
}

/** Shopify pack variants: nearest whole dollar (21.99 → 22.00). */
export function roundPackRrpToDollar(value) {
  const num = typeof value === 'number' ? value : parseFloat(value);
  if (Number.isNaN(num)) return null;
  return formatPrice(Math.round(num));
}

function skuField(sku, camel, snake) {
  if (!sku) return undefined;
  return sku[camel] ?? sku[snake];
}

export function findGridRow(dtcPrices, unitSize, unitPrice) {
  if (!Array.isArray(dtcPrices) || dtcPrices.length === 0) return null;
  const unit = parseFloat(unitSize);
  const price = parseFloat(unitPrice);
  if (Number.isNaN(unit) || Number.isNaN(price)) return null;
  return dtcPrices.find((entry) => (
    parseFloat(entry?.baseUnitPrice) === price
    && parseFloat(entry.baseUnit) === unit
  )) ?? null;
}

/**
 * @param {Array} skus
 * @param {{ containerType: string, unitType: string|number, packType: string|number }} match
 * @returns {object|null}
 */
export function findPackSku(skus, { containerType, unitType, packType }) {
  if (!Array.isArray(skus) || skus.length === 0) return null;
  const ct = String(containerType ?? '').toLowerCase();
  const ut = String(unitType ?? '');
  const pt = String(packType ?? '');
  if (!ct || !ut || !pt) return null;
  return skus.find((sku) => {
    if (sku.deleted || sku.isLegacy) return false;
    const skuCt = String(skuField(sku, 'containerType', 'container_type') ?? '')
      .toLowerCase();
    const skuUt = String(skuField(sku, 'unitType', 'unit_type') ?? '');
    const skuPt = String(skuField(sku, 'packType', 'pack_type') ?? '');
    return skuCt === ct && skuUt === ut && skuPt === pt;
  }) ?? null;
}

/**
 * @returns {{
 *   price: string,
 *   source: 'sku'|'grid'|'formula',
 *   standardPrice: string,
 *   standardSource: 'grid'|'formula',
 *   sku?: { key: *, name: string|null, rrpPack: string|null },
 *   missingRrp?: boolean,
 * }}
 */
export function resolvePackPrice({
  count,
  unitPrice,
  unitSize,
  containerType,
  skus = [],
  dtcPrices = [],
  gridField = null,
  formulaPrice = null,
}) {
  const formula = formatPrice(
    formulaPrice != null ? formulaPrice : parseFloat(unitPrice) * count,
  );
  const gridRow = gridField ? findGridRow(dtcPrices, unitSize, unitPrice) : null;
  const rawGrid = gridField && gridRow ? gridRow[gridField] : null;
  const gridPrice = rawGrid != null && String(rawGrid).trim() !== ''
    ? formatPrice(rawGrid)
    : null;
  const standardPrice = gridPrice ?? formula;
  const standardSource = gridPrice ? 'grid' : 'formula';

  const sku = findPackSku(skus, {
    containerType,
    unitType: unitSize,
    packType: String(count),
  });

  if (sku) {
    const rrp = skuField(sku, 'rrpPack', 'rrp_pack');
    const parsed = parseFloat(rrp);
    const hasRrp = rrp != null && String(rrp).trim() !== '' && !Number.isNaN(parsed);
    const skuMeta = {
      key: sku.key,
      name: sku.name ?? null,
      rrpPack: hasRrp ? formatPrice(parsed) : null,
    };
    if (hasRrp) {
      return {
        price: roundPackRrpToDollar(parsed),
        source: 'sku',
        standardPrice,
        standardSource,
        sku: skuMeta,
      };
    }
    return {
      price: standardPrice,
      source: standardSource,
      standardPrice,
      standardSource,
      sku: skuMeta,
      missingRrp: true,
    };
  }

  return {
    price: standardPrice,
    source: standardSource,
    standardPrice,
    standardSource,
  };
}

function parseSlabFrac(raw, fallback) {
  const n = parseFloat(raw);
  if (Number.isNaN(n) || n < 0 || n >= 1) return fallback;
  return n;
}

function parseSlabCount(raw, fallback) {
  const n = parseFloat(raw);
  if (Number.isNaN(n) || n <= 0) return fallback;
  return n;
}

/** Keep factor (1 − discount) from Guide slab rules. Defaults match Shopify 10%/7.5%. */
function slabKeepFactor(slabCount, rules, { au440 = false } = {}) {
  const threshold = parseSlabCount(rules?.slab_case_large_units, 24);
  const large = parseSlabFrac(rules?.slab_case_discount_large, 0.10);
  const small = parseSlabFrac(rules?.slab_case_discount_small, 0.075);
  if (au440) return 1 - small;
  return parseFloat(slabCount) >= threshold ? 1 - large : 1 - small;
}

function slabMultiplier(slabCount, rules, au440 = false) {
  return slabKeepFactor(slabCount, rules, { au440 });
}

function slabFormulaLabel(slabCount, rules, au440 = false) {
  const keep = slabKeepFactor(slabCount, rules, { au440 });
  const rounded = Math.round(keep * 1000) / 1000;
  return `Unit × n × ${rounded}`;
}

function packWarning(title, resolved) {
  if (!resolved.missingRrp) return null;
  const skuName = resolved.sku?.name || `${title} SKU`;
  const via = resolved.standardSource === 'grid' ? 'grid' : 'formula';
  return (
    `${title} SKU exists but RRP Pack is blank; using ${via} $${resolved.price}. `
    + `Set RRP Pack on "${skuName}" in Product Editor to override.`
  );
}

function pushPack(variants, warnings, title, count, resolved) {
  variants.push({
    title,
    count,
    price: resolved.price,
    source: resolved.source,
    standardPrice: resolved.standardPrice,
    standardSource: resolved.standardSource,
    sku: resolved.sku,
  });
  const warn = packWarning(title, resolved);
  if (warn) warnings.push(warn);
}

function slabRow(unitPrice, slabCount, { au440 = false, rules } = {}) {
  const n = parseFloat(slabCount);
  const mult = slabMultiplier(n, rules, au440);
  const price = formatPrice(parseFloat(unitPrice) * n * mult);
  return {
    title: `${n}-Pack`,
    count: n,
    price,
    source: 'formula',
    standardPrice: price,
    standardSource: 'formula',
    formulaLabel: slabFormulaLabel(n, rules, au440),
  };
}

/**
 * Variant rows matching generateShopifyProductPayload branch logic.
 *
 * @returns {{ variants: object[], warnings: string[] }}
 */
export function resolveDtcVariantPricing({
  store,
  unitSize,
  packConfigKey,
  packType,
  unitPrice,
  slabCount,
  skus = [],
  dtcPrices = [],
  rules,
}) {
  const variants = [];
  const warnings = [];
  const floatedRRP = parseFloat(unitPrice);
  const unitStr = String(unitSize ?? '');
  const customSlab = parseFloat(slabCount);
  const containerType = String(packType ?? '').toLowerCase();
  const unitPriceStr = formatPrice(floatedRRP);

  if (unitPriceStr != null) {
    variants.push({
      title: 'Unit',
      count: 1,
      price: unitPriceStr,
      source: 'unit',
      standardPrice: unitPriceStr,
      standardSource: 'formula',
    });
  }

  if (packType === 'Keg' || Number.isNaN(floatedRRP)) {
    return { variants, warnings };
  }

  const packOpts = {
    unitPrice: floatedRRP,
    unitSize: unitStr,
    containerType,
    skus,
    dtcPrices,
  };

  if (packType === 'Can') {
    pushPack(
      variants,
      warnings,
      '4-Pack',
      4,
      resolvePackPrice({
        ...packOpts,
        count: 4,
        gridField: store === 'GPNZ' ? 'fourPackPrice' : null,
      }),
    );
  }

  const auCanSlab = packType === 'Can'
    && (unitStr === '330' || packConfigKey === '375c')
    && store === 'GPAU';

  if (auCanSlab) {
    variants.push(slabRow(floatedRRP, customSlab, { rules }));
  } else if (packType === 'Bottle' && unitStr === '375') {
    pushPack(
      variants,
      warnings,
      '4-Pack',
      4,
      resolvePackPrice({
        ...packOpts,
        count: 4,
        gridField: null,
        formulaPrice: floatedRRP * 4,
      }),
    );
    const twelve = formatPrice((floatedRRP * 12) - 12);
    variants.push({
      title: '12-Pack',
      count: 12,
      price: twelve,
      source: 'formula',
      standardPrice: twelve,
      standardSource: 'formula',
      formulaLabel: 'Unit × 12 − $12',
    });
  } else if (packType === 'Bottle' && unitStr === '750') {
    pushPack(
      variants,
      warnings,
      '6-Pack',
      6,
      resolvePackPrice({
        ...packOpts,
        count: 6,
        gridField: null,
        formulaPrice: floatedRRP * 6,
      }),
    );
  }

  if (store === 'GPNZ' && packType === 'Can') {
    pushPack(
      variants,
      warnings,
      '6-Pack',
      6,
      resolvePackPrice({
        ...packOpts,
        count: 6,
        gridField: 'sixPackPrice',
      }),
    );
    variants.push(slabRow(floatedRRP, customSlab, { rules }));
  } else if (store === 'GPAU' && unitStr === '440') {
    variants.push(slabRow(floatedRRP, customSlab, { au440: true, rules }));
  }

  return { variants, warnings };
}
